import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import {
  type ResumeEmployment,
  type ResumeParsingStatus,
} from 'talent_matching/types';
import { withLocalePrefix } from 'shared/utils/translation';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import styled from 'styled-components';

const t = withLocalePrefix('talent_matching.resume_details');

const DEFAULT_MAX_DISPLAYED = 3;

const CurrentJob = styled.span<{ show: boolean }>`
  border: 1px solid ${FullPalette.GREY_700};
  border-radius: 5px;
  margin-left: 5px;
  padding: 0 5px;
  display: ${(props) => (props.show ? 'inline' : 'none')};
`;

const Employment = ({ employment }: { employment: ResumeEmployment }) => {
  const formatDate = (date: string) => {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
    });
  };

  let companyInfo = employment.employer || '';
  if (employment.start_date) {
    companyInfo += `${companyInfo ? ' · ' : ''}`;
    companyInfo += `${formatDate(employment.start_date)} -`;

    if (employment.end_date) {
      companyInfo += ` ${formatDate(employment.end_date)}`;
    }
  } else {
    companyInfo += `${companyInfo ? ' · ' : ''}`;
    companyInfo += `[${t('not_listed')}]`;

    if (employment.end_date) {
      companyInfo += ` - ${formatDate(employment.end_date)}`;
    }
  }

  const showCurrentJob = Boolean(employment.start_date && !employment.end_date);

  return (
    <Stack direction="column" spacing={0}>
      <SecondaryBody className="pii-blur">{employment.title}</SecondaryBody>
      <SecondaryBody metadata>
        <span className="pii-blur">{companyInfo}</span>
        <CurrentJob show={showCurrentJob}>{t('current')}</CurrentJob>
      </SecondaryBody>
    </Stack>
  );
};

const PARSING_MESSAGES: Record<ResumeParsingStatus, string> = {
  in_progress: 'parsing_in_progress',
  complete: 'no_employment_history',
  failed: 'parsing_failed',
};

type Props = {
  employments: ResumeEmployment[];
  parsingStatus?: ResumeParsingStatus;
  resumeExists?: boolean;
  onShowMore?: () => void;
  maxDisplayed?: number;
};

const BaseResumeDetailsCell = ({
  employments,
  parsingStatus,
  resumeExists,
  onShowMore,
  maxDisplayed = DEFAULT_MAX_DISPLAYED,
}: Props) => {
  const totalEmployments = employments.length;

  if (!totalEmployments) {
    const statusKey = PARSING_MESSAGES[parsingStatus || 'in_progress'];
    const statusMessage = resumeExists ? t(statusKey) : t('no_resume');

    return (
      <div style={{ height: '100%' }}>
        <Stack
          spacing={1}
          sx={{
            padding: 1,
            paddingLeft: 0,
            height: '100%',
          }}
        >
          <SecondaryBody metadata>{statusMessage}</SecondaryBody>
        </Stack>
      </div>
    );
  }

  return (
    <div style={{ height: '100%' }}>
      <Stack
        sx={{
          padding: 1,
          paddingLeft: 0,
          height: '100%',
        }}
      >
        <Stack direction="column" spacing={1}>
          {employments.slice(0, maxDisplayed).map((employment, index) => (
            <Employment key={index} employment={employment} />
          ))}
          {totalEmployments > maxDisplayed && onShowMore && (
            <div
              style={{
                whiteSpace: 'nowrap',
                display: 'flex',
                alignSelf: 'flex-start',
              }}
            >
              <TertiaryButton size="md" onClick={onShowMore}>
                {t('more', {
                  count: totalEmployments - maxDisplayed,
                })}
              </TertiaryButton>
            </div>
          )}
        </Stack>
      </Stack>
    </div>
  );
};

export default BaseResumeDetailsCell;
